import { ref, computed, reactive, Ref, ComputedRef } from "vue";
import { defineStore } from "pinia";
import {
  AppConfigConstants,
  AppConfigDistrictResponse,
  HomepageConfig,
  LookupTableItem,
  LookupTableItemKeys,
  LookupTables,
  LookupTablesKeys,
  NestedLookupTableItem,
  NestedLookupTables,
  NestedLookupTablesKeys,
  SearchSuggestions,
  StateCommitteeBucketResponse,
  SubscriptionModule,
  VignetteHelpers,
} from "@/types";
import { ParsedResponse } from "@/types/utils/CaseTypes";

// Set app base url, used in ApiService as well
const basePath = process.env.VUE_APP_BASE_URL || "/";
// Used if origin is not available (IE11 Compatibility View)
let baseOriginFallback = window.location.protocol + "//" + window.location.host;
if (baseOriginFallback.indexOf("undefined") >= 0) {
  baseOriginFallback = "https://www.nationaljournal.com";
}
const baseOrigin = (window.location.origin || baseOriginFallback) + basePath;

export const useAppStore = defineStore("app", () => {
  const baseUrlPath = ref(basePath);
  const djangoSiteOrigin = VignetteHelpers.getBaseOriginForDjangoSite();
  const baseUrlOrigin = ref(baseOrigin);
  const redirectPath = ref("");

  const pageName = ref("");

  const loading = ref(true);

  // HOMEPAGE CONFIG and GETTERS
  const homePageConfig: HomepageConfig = reactive({
    isLoading: false,
    lastUpdated: null,
    featuredProfiles: [],
    featuredProfilesText: "",
    recentProfiles: [],
    noteworthyGroups: [],
    featuredState: null,
    featuredDistrict: null,
    primaryBanner: { wideImg: "", smallImg: "", url: "" },
    secondaryBanner: { wideImg: "", smallImg: "", url: "" },
  });
  const isHomepageFeaturedProfilesLoading = computed(() => {
    return homePageConfig.isLoading;
  });
  const getHomepageConfigLastUpdated = computed(() => {
    return homePageConfig.lastUpdated;
  });
  const getHomepageConfigFeaturedProfiles = computed(() => {
    return homePageConfig.featuredProfiles;
  });
  const getHomepageConfigFeaturedProfilesText = computed(() => {
    return homePageConfig.featuredProfilesText;
  });
  const getHomepageConfigRecentProfiles = computed(() => {
    return homePageConfig.recentProfiles;
  });
  const getHomepageConfigNoteworthyGroups = computed(() => {
    return homePageConfig.noteworthyGroups;
  });
  const getHomepageConfigPrimaryBanner = computed(() => {
    return homePageConfig.primaryBanner;
  });
  const getHomepageConfigSecondaryBanner = computed(() => {
    return homePageConfig.secondaryBanner;
  });

  function clearHomePageProfilesAndGroups() {
    homePageConfig.featuredProfiles = [];
    homePageConfig.recentProfiles = [];
    homePageConfig.noteworthyGroups = [];
  }

  /**
   * This ref holds the values of the state legislative job categories received from
   * /appConfig endpoint under 'state_leg_job_categories'
   */
  const stateLegJobCategories = ref<string[]>([]);

  const searchSuggestions = reactive<SearchSuggestions>({
    Universal: "",
    Congressional: "",
    Federal: "",
    State: "",
  });

  /**
   * This ref holds a mapping of the legislative chambers and their ideas
   */
  const legislativeChambers = ref({
    US_HOUSE: 1,
    US_SENATE: 2,
    STATE_HOUSE: 3,
    STATE_SENATE: 4,
    US_CONGRESS_JOINT: 5,
    STATE_JOINT: 6,
  });

  const districts = ref<AppConfigDistrictResponse[]>([]);

  // Optional map provided by appConfig for state committees
  const stateCommitteesMap = reactive<Record<string, ParsedResponse<StateCommitteeBucketResponse>>>({});
  const stateCommitteesFlatLookupMap = reactive(new Map<string, { id: string; name: string }>());

  interface DistrictMap {
    US_HOUSE: AppConfigDistrictResponse[];
    STATE_HOUSE: AppConfigDistrictResponse[];
    STATE_SENATE: AppConfigDistrictResponse[];
    STATE_COMBINED: AppConfigDistrictResponse[];
  }
  const districtMap: DistrictMap = reactive({
    US_HOUSE: [],
    STATE_HOUSE: [],
    STATE_SENATE: [],
    STATE_COMBINED: [],
  });

  const constants = ref<ParsedResponse<AppConfigConstants>>();

  // NON-NESTED LOOKUP TABLES
  const _getDefaultLookupTables = () => {
    return {
      appointments: [],
      countries: [],
      degrees: [],
      districts: [],
      editorialGroups: [],
      ethnicities: [],
      focusAreas: [],
      genders: [],
      industries: [],
      jobCategories: [],
      jobCategoriesFederal: [],
      maritalStatuses: [],
      politicalParties: [],
      positionStatuses: [],
      primaryProfileTypes: [],
      religions: [],
      secondaryProfileTypes: [],
      states: [],
      subscriptionModules: [],

      // Flattened nested lookup tables

      agenciesFlat: [],
      caucusesFlat: [],
      committeesFlat: [],
      industriesFlat: [],
      keyPolicyAreasFlat: [],
      profileTypesFlat: [],
      specialInterestGroupsFlat: [],
      /** Generated & hardcoded fields **/

      // Birthdate
      birthYearOptions: [], // generated on init
      birthYearOperator: [
        { id: "exact", name: "Born On" },
        { id: "lt", name: "Born Before" },
        { id: "gte", name: "Born After" },
      ],
      ageOptions: [], // generated on init
      ageOperator: [
        { id: "exact", name: "Exact" },
        { id: "lt", name: "Younger Than" },
        { id: "gte", name: "Older Than" },
      ],

      // Special Interest Groups
      sigOperator: [
        { id: "lte", name: "Less Than" },
        { id: "gte", name: "Greater Than" },
      ],
      sigRatingOptions: [], // generated on init
    };
  };
  const lookupTables: Ref<LookupTables> = ref(_getDefaultLookupTables());
  /**
   * Returns the lookup table for the given table name.
   *
   * @param {LookupTablesKeys} tableName - The name of the lookup table to retrieve.
   * @returns {LookupTableItem[]} - The lookup table corresponding to the given table name.
   */
  const getLookupTable = (tableName: LookupTablesKeys): LookupTableItem[] =>
    lookupTables.value[tableName];
  /**
   * Retrieves a lookup table entry based on the given type and search query.
   *
   * @param {LookupTablesKeys} table - The key of the lookup table to search within.
   * @param {LookupTableItemKeys} searchType - The key of the item property to match against the query.
   * @param {string} query - The value to search for within the given search type.
   * @returns {LookupTableItem} - The computed reference to the found lookup table item, or an object with empty strings if no match was found.
   */
  const getLookupTableEntryByType = (
    table: LookupTablesKeys,
    searchType: LookupTableItemKeys,
    query: string
  ): LookupTableItem =>
    lookupTables.value[table].find((item) => item[searchType] === query) || {
      id: "",
      name: "",
    };
  /**
   * Retrieves a nullable lookup table entry by type.
   *
   * @param {LookupTablesKeys} table - The key of the lookup table to search in.
   * @param {LookupTableItemKeys} searchType - The key of the property to search for in the lookup table items.
   * @param {string} query - The value to search for in the specified property.
   *
   * @returns {LookupTableItem | null} - A computed reference to the matching lookup table item, or null if not found.
   */
  const getNullableLookupTableEntryByType = (
    table: LookupTablesKeys,
    searchType: LookupTableItemKeys,
    query: string
  ): LookupTableItem | null =>
    lookupTables.value[table].find((item) => item[searchType] === query) ||
    null;
  /**
   * Retrieves the subscription modules.
   * @returns {ComputedRef<SubscriptionModule[]>} The computed reference to the subscription modules.
   */
  const getSubscriptionModules = (): ComputedRef<SubscriptionModule[]> =>
    computed(() => {
      return lookupTables.value.subscriptionModules;
    });
  /**
   * Retrieve the name of a subscription module based on its ID.
   * @param {number} id - The ID of the subscription module.
   * @returns {ComputedRef<string>} - A reactive computed reference containing the name of the subscription module.
   */
  const getSubscriptionModuleName = (id: number): ComputedRef<string> =>
    computed(() => {
      return (
        lookupTables.value.subscriptionModules.find(
          (module) => module.id === id
        )?.name ?? ""
      );
    });
  /**
   * Retrieves the abbreviation of a subscription module based on its ID.
   *
   * @param {number} id - The ID of the subscription module.
   * @returns {ComputedRef<string>} - The computed reference to the module's abbreviation.
   */
  const getSubscriptionModuleAbbreviation = (id: number): ComputedRef<string> =>
    computed(() => {
      return (
        lookupTables.value.subscriptionModules.find(
          (module) => module.id === id
        )?.abbreviation ?? ""
      );
    });

  /**
   * Generates lookup tables for birth year, age, and significance rating options.
   * @returns {void}
   */
  function setLookupTablesGeneratedFields(): void {
    for (let year = 2020; year >= 1920; year--) {
      lookupTables.value.birthYearOptions.push({
        id: year.toString(),
        name: year.toString(),
      });
    }
    for (let age = 18; age < 100; age++) {
      lookupTables.value.ageOptions.push({
        id: age.toString(),
        name: age.toString(),
      });
    }
    for (let score = 0; score < 100; score += 10) {
      lookupTables.value.sigRatingOptions.push({
        id: score.toString(),
        name: score.toString() + "%",
      });
    }
  }

  // NESTED LOOKUP TABLES
  const _getDefaultNestedLookupTables = () => {
    return {
      profileTypes: [],
      keyPolicyAreas: [],
      industries: [],
      committees: [],
      caucuses: [],
      agencies: [],
      specialInterestGroups: [],
    };
  };
  const nestedLookupTables: Ref<NestedLookupTables> = ref(
    _getDefaultNestedLookupTables()
  );
  /**
   * Retrieves a nested lookup table based on the provided table name.
   *
   * @param {NestedLookupTablesKeys} tableName - The name of the table to retrieve.
   * @returns {NestedLookupTableItem[]} - The retrieved nested lookup table.
   */
  const getNestedLookupTable = (
    tableName: NestedLookupTablesKeys
  ): NestedLookupTableItem[] => nestedLookupTables.value[tableName];

  const unlockedProfiles: Ref<number[]> = ref([]);

  return {
    baseUrlPath,
    djangoSiteOrigin,
    baseUrlOrigin,
    redirectPath,

    pageName,

    loading,

    homePageConfig,
    isHomepageFeaturedProfilesLoading,
    getHomepageConfigLastUpdated,
    getHomepageConfigFeaturedProfiles,
    getHomepageConfigFeaturedProfilesText,
    getHomepageConfigRecentProfiles,
    getHomepageConfigNoteworthyGroups,
    getHomepageConfigPrimaryBanner,
    getHomepageConfigSecondaryBanner,
    clearHomePageProfilesAndGroups,

    searchSuggestions,
    stateLegJobCategories,
    legislativeChambers,
    districts,
    stateCommitteesMap,
    stateCommitteesFlatLookupMap,
    districtMap,
    constants,

    lookupTables,
    getLookupTable,
    getLookupTableEntryByType,
    getNullableLookupTableEntryByType,
    getSubscriptionModules,
    getSubscriptionModuleName,
    getSubscriptionModuleAbbreviation,
    setLookupTablesGeneratedFields,

    nestedLookupTables,
    getNestedLookupTable,

    unlockedProfiles,
  };
});
